import React, {useEffect, useRef, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Alert, Spinner} from "react-bootstrap";
import {useForm} from "react-hook-form";
import styles from "./KBModal.module.css";
import stylesInputs from "../../../assets/styles/inputs.module.css";
import {Role} from "../../../services/models/role";
import {IFolder} from "../../../types/knowledgeBase.types";
import {SelectOption} from "../../shared/modalStyles";
import {useCreateKBFolder} from "../../../hooks/useQuery/useCreateKBFolder";
import {useUpdateKBFolder} from "../../../hooks/useQuery/useUpdateKBFolder";
import {useDeleteKBFolder} from "../../../hooks/useQuery/useDeleteKBFolder";
import {useUploadKBFile} from "../../../hooks/useQuery/useUploadKBFile";
import {useUploadKBVideo} from "../../../hooks/useQuery/useUploadKBVideo";
import {useCreateKBFileLink} from "../../../hooks/useQuery/useCreateKBFileLink";
import {useNavigate} from "react-router-dom";
import {ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_VIDEO_SIZE} from "../../../Utils/uploadFileSettings";
import {RoleSelect} from "../../shared/RoleSelect/RoleSelect";
import {FormActionButton} from "../../shared/buttons/FormActionButton";
import {generateVideoThumbnail} from "../../../Utils/videoUtils";
import {useFetchSectors} from "../../../hooks/useQuery";


type ModalMode = 'createFolder' | 'editFolder' | 'uploadFile' | 'uploadVideo';

interface KBModalProps {
    show: boolean;
    onHide: () => void;
    roles: Role[];
    mode: ModalMode;
    currentFolder?: IFolder;
}

interface FormData {
    name: string;
    roles: SelectOption[];
    file?: FileList;
    link?: string;
}

const modalTitles = {
    createFolder: 'Создать папку',
    editFolder: 'Редактировать папку',
    uploadFile: 'Загрузить файл',
    uploadVideo: 'Загрузить видео файл'
};

export const KBModal: React.FC<KBModalProps> = ({
                                                    show = false,
                                                    onHide = () => {
                                                    },
                                                    roles = [],
                                                    mode,
                                                    currentFolder,
                                                }) => {
    const {
        register,
        handleSubmit,
        formState: {errors},
        reset,
        setValue,
        watch
    } = useForm<FormData>({
        mode: "onChange",
        defaultValues: {
            name: mode === 'editFolder' ? currentFolder?.name || '' : '',
            roles: []
        }
    });

    const navigate = useNavigate();
    const [errorText, setErrorText] = useState<string | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<SelectOption[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileIsLink, setFileIsLink] = useState(false);
    const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [toggleValue, setToggleValue] = useState<"role" | "region">("role")
    // Инициализация выбранных ролей при редактировании папки
    useEffect(() => {
        if (mode === 'editFolder') {
            if (currentFolder?.accessType === 'role' && currentFolder?.guestRoles) {
                setSelectedRoles(
                    currentFolder.guestRoles.map(role => ({
                        value: role.id,
                        label: role.name,
                        fullLabel: role.name
                    }))
                );
                setToggleValue('role');
            } else if (currentFolder?.accessType === 'region' && currentFolder?.guestRegions) {
                setSelectedRegions(
                    currentFolder.guestRegions.map(region => ({
                        value: region.id,
                        label: region.name,
                        fullLabel: region.name
                    }))
                );
                setToggleValue('region');
            }
        } else {
            setSelectedRoles([]);
            setSelectedRegions([]);
            setToggleValue('role');
        }
    }, [currentFolder, mode]);
    
    const isSubmitDisabled = () => {
        const nameValue = watch("name");
        const linkValue = watch("link");

        // Общая проверка для всех режимов - название должно быть не пустым
        if (!nameValue?.trim()) {
            return true;
        }

        // Для создания и редактирования папки
        if (mode === 'createFolder' || mode === 'editFolder') {
            if (toggleValue === 'role') {
                return selectedRoles.length === 0;
            } else {
                return selectedRegions.length === 0;
            }
        }

        // Для загрузки файла
        if (mode === 'uploadFile') {
            if (fileIsLink) {
                return !linkValue?.trim();
            } else {
                return !selectedFile;
            }
        }

        // Для загрузки видео
        if (mode === 'uploadVideo') {
            // Для видео требуется файл и превью (которое генерируется автоматически)
            return !selectedFile || isUploading;
        }

        return false;
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const createFolder = useCreateKBFolder();
    const updateFolder = useUpdateKBFolder(currentFolder?._id);
    const deleteFolder = useDeleteKBFolder();
    const uploadFile = useUploadKBFile();
    const uploadVideo = useUploadKBVideo();
    const createLink = useCreateKBFileLink();

    const handleClose = () => {
        reset();
        setSelectedRoles([]);
        setErrorText(null);
        setSelectedFile(null);
        setUploadError(null);
        setIsUploading(false);
        onHide();
    };

    const validateFile = (file: File): string | null => {


        // Проверяем размер файла в зависимости от режима
        if (mode === 'uploadVideo') {
            if (!ALLOWED_FILE_TYPES.VIDEO.includes(file.type)) {
                return 'Недопустимый тип файла';
            }
            if (file.size > MAX_VIDEO_SIZE) {
                return 'Размер видео превышает 100 МБ';
            }
        } else {
            if (!ALLOWED_FILE_TYPES.FILES.includes(file.type)) {
                return 'Недопустимый тип файла';
            }
            if (file.size > MAX_FILE_SIZE) {
                return 'Размер файла превышает 10 МБ';
            }
        }

        return null;
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            setSelectedFile(null);
            setUploadError(null);
            setThumbnailBlob(null);
            setThumbnailUrl(null);
            return;
        }

        const file = files[0];
        const error = validateFile(file);

        if (error) {
            setUploadError(error);
            setSelectedFile(null);
            setThumbnailBlob(null);
            setThumbnailUrl(null);
        } else {
            setUploadError(null);
            setSelectedFile(file);

            // Если это видео, генерируем превью
            if (mode === 'uploadVideo' && file.type.startsWith('video/')) {
                try {
                    setIsUploading(true); // Показываем индикатор загрузки во время генерации превью
                    const blob = await generateVideoThumbnail(file);
                    setThumbnailBlob(blob);
                    setThumbnailUrl(URL.createObjectURL(blob));
                    setIsUploading(false);
                } catch (error) {
                    console.error('Ошибка при создании превью:', error);
                    setIsUploading(false);
                }
            }
        }
    };

    const formatFileSize = (bytes: number): string => {
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const onSubmit = async (data: FormData) => {
        try {
            setErrorText(null);

            switch (mode) {
                case 'createFolder': {
                    const payload = {
                        name: data.name,
                        accessType: toggleValue,
                        guestRoles: toggleValue === 'role' ? selectedRoles.map(role => role.value) : [],
                        guestRegions: toggleValue === 'region' ? selectedRegions.map(region => region.value) : [],
                        coverFile: selectedFile || undefined,
                        parentFolder: currentFolder?._id
                    };
                    await createFolder.mutateAsync(payload);
                    break;
                }
                case 'editFolder': {
                    if (!currentFolder?._id) return;
                    const payload = {
                        _id: currentFolder._id,
                        name: data.name,
                        accessType: toggleValue,
                        guestRoles: toggleValue === 'role' ? selectedRoles.map(role => role.value) : [],
                        guestRegions: toggleValue === 'region' ? selectedRegions.map(region => region.value) : [],
                        coverFile: selectedFile || undefined
                    };
                    await updateFolder.mutateAsync(payload);
                    break;
                }

                case 'uploadFile':
                    if (!currentFolder?._id) {
                        setErrorText('Не указана папка для загрузки');
                        return;
                    }

                    setIsUploading(true);

                    if (fileIsLink) {
                        // Проверяем, что ссылка указана
                        if (!data.link) {
                            setErrorText('Пожалуйста, введите ссылку');
                            setIsUploading(false);
                            return;
                        }

                        // Используем хук useCreateKBFileLink для создания ссылки
                        await createLink.mutateAsync({
                            folderId: currentFolder._id,
                            data: {
                                name: data.name,
                                link: data.link
                            }
                        });
                    } else {
                        // Для обычных файлов проверяем наличие файла
                        if (!selectedFile) {
                            setErrorText('Пожалуйста, выберите файл');
                            setIsUploading(false);
                            return;
                        }

                        // Загружаем обычный файл
                        await uploadFile.mutateAsync({
                            folderId: currentFolder._id,
                            data: {
                                name: data.name,
                                file: selectedFile
                            }
                        });
                    }
                    break;

                case 'uploadVideo':
                    if (!currentFolder?._id) {
                        setErrorText('Не указана папка для загрузки');
                        return;
                    }

                    setIsUploading(true);

                    // Для видео проверяем наличие файла и превью
                    if (!selectedFile) {
                        setErrorText('Пожалуйста, выберите видео');
                        setIsUploading(false);
                        return;
                    }

                    // Создаем File из Blob для превью, если оно есть
                    let thumbnailFile: File | undefined = undefined;
                    if (thumbnailBlob) {
                        thumbnailFile = new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
                    }
                    
                    // Загружаем видео
                    await uploadVideo.mutateAsync({
                        folderId: currentFolder._id,
                        data: {
                            name: data.name,
                            file: selectedFile,
                            type: 'video',
                            thumbnail: thumbnailFile
                        }
                    });
                    break;
            }

            handleClose();
        } catch (error) {
            console.error('Error:', error);
            setErrorText('Произошла ошибка. Пожалуйста, попробуйте снова.');
        } finally {
            setIsUploading(false);
        }
    };

    const handlerDeleteFolder = async () => {
        if (!currentFolder?._id) return;
        await deleteFolder.mutateAsync(currentFolder._id);
        navigate(-1);
        handleClose();
    };
    const { sectorsData, sectorsError, sectorsIsLoading } = useFetchSectors();

    return (
        <Modal
            show={show}
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            dialogClassName={styles.eventModalDialog}
            contentClassName={styles.modalContent}
        >
            <Modal.Header closeButton className={styles.modalHeader}>
                <Modal.Title id="contained-modal-title-vcenter">{modalTitles[mode]}</Modal.Title>
            </Modal.Header>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body className={styles.modalBody}>
                    {errorText ? (
                        <Alert variant="danger" className="mb-3">
                            {errorText}
                        </Alert>
                    ) : null}

                    {(mode === 'createFolder' || mode === 'editFolder') && (
                        <>
                            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                                <label className={stylesInputs.label}>
                                    Название папки
                                </label>
                                <div className={stylesInputs.inputWrapper}>
                                    <input
                                        type="text"
                                        className={stylesInputs.mainInput}
                                        placeholder={"Название папки"}
                                        {...register("name", {required: true})}
                                    />
                                </div>
                                {errors.name && (
                                    <span className={stylesInputs.error}>
                                        Это поле обязательно
                                    </span>
                                )}
                            </div>
                            <div className="mb-3">
                                <div className={styles.toggleWrapper}>
                                    <span className={styles.toggleLabel + (toggleValue === 'role' ? ' ' + styles.active : '')}>
                                        Доступ по ролям
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.toggleButton + ' ' + (toggleValue === 'region' ? styles.toggled : '')}
                                        onClick={() => setToggleValue(toggleValue === 'role' ? 'region' : 'role')}
                                        aria-label="Переключить доступ"
                                    >
                                        <span className={styles.toggleSlider} />
                                    </button>
                                    <span className={styles.toggleLabel + (toggleValue === 'region' ? ' ' + styles.active : '')}>
                                        Доступ по регионам
                                    </span>
                                </div>
                            </div>

                            {toggleValue === 'role' && (
                                <div className="mb-3">
                                    <label className={stylesInputs.label}>
                                        Выберите роли
                                    </label>
                                    <RoleSelect
                                        selectedRoles={selectedRoles}
                                        setSelectedRoles={setSelectedRoles}
                                        options={roles.map(role => ({
                                            value: role.id,
                                            label: role.name,
                                            fullLabel: role.name
                                        }))}
                                        className={styles.selectWrapper}
                                    />
                                </div>
                            )}
                            {toggleValue === 'region' && (
                                <div className="mb-3">
                                    <label className={stylesInputs.label}>
                                        Выберите регионы
                                    </label>
                                    <RoleSelect
                                        placeholder={"Выберите регионы"}
                                        selectedRoles={selectedRegions}
                                        setSelectedRoles={setSelectedRegions}
                                        options={sectorsData ? sectorsData.map(sector => ({
                                            value: sector.id,
                                            label: sector.name,
                                            fullLabel: sector.name
                                        })) : []}
                                        className={styles.selectWrapper}
                                        isSearchable={true}
                                    />
                                </div>
                            )}

                            <div className={stylesInputs.formGroup}>
                                <div
                                    className={`${stylesInputs.fileUpload} ${
                                        !selectedFile
                                            ? ''
                                            : uploadError
                                                ? stylesInputs.error
                                                : stylesInputs.success
                                    }`}
                                >
                                    <input
                                        type="file"
                                        className={stylesInputs.fileUploadInput}
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />

                                    <div
                                        className={` ${stylesInputs.fileUploadContent} ${selectedFile ? stylesInputs.withFile : ''}`}>
                                        {!selectedFile ? (
                                            <span>Загрузить обложку</span>
                                        ) : (
                                            <>
                                                <div className={stylesInputs.fileInfo}>
                                                    <span>{selectedFile.name}</span>
                                                    <span className={stylesInputs.fileSize}>
                                                    {formatFileSize(selectedFile.size)}
                                                    </span>
                                                </div>
                                                <div className={stylesInputs.fileActions}>
                                                    {isUploading && (
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                </div>
                                {!selectedFile && (
                                    <label className={stylesInputs.label}>
                                        Размер файла не должен превышать 10 МБ
                                    </label>
                                )}
                            </div>
                        </>
                    )}

                    {mode === 'uploadFile' && (
                        <>
                            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                                <label className={stylesInputs.label}>
                                    Название файла
                                </label>
                                <div className={stylesInputs.inputWrapper}>
                                    <input
                                        type="text"
                                        className={`${stylesInputs.mainInput} ${errors.name ? stylesInputs.error : ''}`}
                                        {...register("name", {required: true})}
                                        placeholder="Введите название"
                                    />
                                </div>
                                {errors.name && (
                                    <span className={stylesInputs.error}>
                                        Это поле обязательно
                                    </span>
                                )}
                            </div>

                            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                                <label className={`mb-3 ${styles.checkboxContainer}`}>
                                    <input
                                        type="checkbox"
                                        id="fileIsLink"
                                        checked={fileIsLink}
                                        onChange={(e) => {
                                            setFileIsLink(e.target.checked);
                                            if (e.target.checked) {
                                                // Если переключились на ссылку, сбрасываем выбранный файл
                                                setSelectedFile(null);
                                                setUploadError(null);
                                            }
                                        }}
                                        className={styles.checkbox}
                                    />
                                    <span className={styles.checkmark}></span>
                                    Файл является ссылкой
                                </label>
                            </div>

                            {fileIsLink ? (
                                <div className={stylesInputs.formGroup}>
                                    <label className={stylesInputs.label}>Ссылка</label>
                                    <div className={stylesInputs.inputWrapper}>
                                        <input
                                            type="text"
                                            className={`${stylesInputs.mainInput} ${errors.link ? stylesInputs.error : ''}`}
                                            {...register("link", {required: fileIsLink})}
                                            placeholder="Введите ссылку"
                                        />
                                    </div>
                                    {errors.link && (
                                        <span className={stylesInputs.error}>
                                            Это поле обязательно
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className={stylesInputs.formGroup}>
                                    <div
                                        className={`${stylesInputs.fileUpload} ${
                                            !selectedFile
                                                ? ''
                                                : uploadError
                                                    ? stylesInputs.error
                                                    : stylesInputs.success
                                        }`}
                                    >
                                        <input
                                            type="file"
                                            className={stylesInputs.fileUploadInput}
                                            onChange={handleFileChange}
                                            ref={fileInputRef}
                                        />

                                        <div
                                            className={` ${stylesInputs.fileUploadContent} ${selectedFile ? stylesInputs.withFile : ''}`}>
                                            {!selectedFile ? (
                                                <span>Загрузить файл</span>
                                            ) : (
                                                <>
                                                    <div className={stylesInputs.fileInfo}>
                                                        <span>{selectedFile.name}</span>
                                                        <span className={stylesInputs.fileSize}>
                                                        {formatFileSize(selectedFile.size)}
                                                    </span>
                                                    </div>
                                                    <div className={stylesInputs.fileActions}>
                                                        {isUploading && (
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {!selectedFile && (
                                        <label className={stylesInputs.label}>
                                            Размер файла не должен превышать 10 МБ
                                        </label>
                                    )}
                                    {uploadError && (
                                        <span className={stylesInputs.error}>
                                            {uploadError}
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {mode === 'uploadVideo' && (
                        <>
                            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                                <label className={stylesInputs.label}>
                                    Название видеофайла
                                </label>
                                <div className={stylesInputs.inputWrapper}>
                                    <input
                                        type="text"
                                        className={`${stylesInputs.mainInput} ${errors.name ? stylesInputs.error : ''}`}
                                        {...register("name", {required: true})}
                                        placeholder="Введите название"
                                    />
                                </div>
                                {errors.name && (
                                    <span className={stylesInputs.error}>
                                    Это поле обязательно
                                </span>
                                )}
                            </div>

                            <div className={stylesInputs.formGroup}>
                                <div
                                    className={`${stylesInputs.fileUpload} ${
                                        !selectedFile
                                            ? ''
                                            : uploadError
                                                ? stylesInputs.error
                                                : stylesInputs.success
                                    }`}
                                >
                                    <input
                                        type="file"
                                        className={stylesInputs.fileUploadInput}
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />

                                    <div
                                        className={` ${stylesInputs.fileUploadContent} ${selectedFile ? stylesInputs.withFile : ''}`}>
                                        {!selectedFile ? (
                                            <span>Загрузить видео</span>
                                        ) : (
                                            <>
                                                <div className={stylesInputs.fileInfo}>
                                                    <span>{selectedFile.name}</span>
                                                    <span className={stylesInputs.fileSize}>
                                                        {formatFileSize(selectedFile.size)}
                                                    </span>
                                                </div>
                                                <div className={stylesInputs.fileActions}>
                                                    {isUploading && (
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {!selectedFile && (
                                    <label className={stylesInputs.label}>
                                        Размер файла не должен превышать 100 МБ
                                    </label>
                                )}
                                {uploadError && (
                                    <span className={stylesInputs.error}>
                                            {uploadError}
                                        </span>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>

                <div className={`d-flex gap-3 ${styles.buttonsWrapper}`}>
                    {mode === "editFolder" && (
                        <FormActionButton
                            mode={"danger"}
                            text={"Удалить папку"}
                            onClick={handlerDeleteFolder}
                        />
                    )}
                    <FormActionButton
                        mode={"success"}
                        text={isUploading ? 'Загрузка...' : 'Сохранить'}
                        type="submit"
                        disabled={isUploading || isSubmitDisabled()}
                    />
                </div>
            </form>
        </Modal>
    )
        ;
};